import React, { useState, useEffect } from 'react';
import { XCircle } from 'lucide-react';

const RevisionSettings = ({ onClose, onApply, defaultSettings = { differentModel: 'no', flexibility: 'low' } }) => {
  const [flexibility, setFlexibility] = useState(defaultSettings.flexibility || 'low');
  const [differentModel, setDifferentModel] = useState(defaultSettings.differentModel || 'no');
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  // Update button state whenever settings change
  useEffect(() => {
    // Create original settings reference inside the effect to avoid dependency issues
    const originalFlexibility = defaultSettings.flexibility || 'low';
    const originalDifferentModel = defaultSettings.differentModel || 'no';
    
    // Button should be enabled if the current settings are different from original settings
    const settingsChanged = 
      flexibility !== originalFlexibility || 
      differentModel !== originalDifferentModel;
      
    setIsButtonDisabled(!settingsChanged);
  }, [flexibility, differentModel, defaultSettings]);

  const handleApply = () => {
    onApply({ differentModel, flexibility });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        {/* Sticky Navbar */}
        <div className="sticky top-0 bg-white border-b border-gray-300 p-4 flex justify-between items-center rounded-t-lg">
          <h1 className="text-xl font-semibold">Adjust Revision Settings</h1>
          <button 
            className="p-2 rounded-full hover:bg-gray-100"
            onClick={onClose}
          >
            <XCircle className="w-6 h-6 text-black" />
          </button>
        </div>

        <div className="p-8 space-y-8">
          {/* Section 1: Increase model flexibility */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-black text-white font-bold">1</div>
              <h2 className="text-lg font-semibold text-black">Increase model flexibility</h2>
            </div>
            <div className="flex space-x-4">
              {/* Option: Low (default) */}
              <label className="cursor-pointer block">
                <input 
                  type="radio" 
                  name="modelFlexibility" 
                  value="low" 
                  className="hidden peer" 
                  checked={flexibility === 'low'}
                  onChange={() => setFlexibility('low')}
                />
                <div className="w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col justify-center items-center p-4 border-2 border-transparent peer-checked:border-black">
                  <span className="text-sm font-medium text-black">Low</span>
                </div>
              </label>
              {/* Option: Medium */}
              <label className="cursor-pointer block">
                <input 
                  type="radio" 
                  name="modelFlexibility" 
                  value="medium" 
                  className="hidden peer"
                  checked={flexibility === 'medium'}
                  onChange={() => setFlexibility('medium')}
                />
                <div className="w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col justify-center items-center p-4 border-2 border-transparent peer-checked:border-black">
                  <span className="text-sm font-medium text-black">Medium</span>
                </div>
              </label>
              {/* Option: High */}
              <label className="cursor-pointer block">
                <input 
                  type="radio" 
                  name="modelFlexibility" 
                  value="high" 
                  className="hidden peer"
                  checked={flexibility === 'high'}
                  onChange={() => setFlexibility('high')}
                />
                <div className="w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col justify-center items-center p-4 border-2 border-transparent peer-checked:border-black">
                  <span className="text-sm font-medium text-black">High</span>
                </div>
              </label>
            </div>
            <p className="mt-6 text-xs text-gray-700" style={{ visibility: flexibility === 'low' ? 'hidden' : 'visible', height: '20px' }}>
              Higher flexibility may cause the model to follow your instructions less closely.
            </p>
          </div>

          {/* Section 2: Use a different model */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-black text-white font-bold">2</div>
              <h2 className="text-lg font-semibold text-black">Use a different model</h2>
            </div>
            <div className="flex space-x-4">
              {/* Option: No (default) */}
              <label className="cursor-pointer block">
                <input 
                  type="radio" 
                  name="differentModel" 
                  value="no" 
                  className="hidden peer" 
                  checked={differentModel === 'no'}
                  onChange={() => setDifferentModel('no')}
                />
                <div className="w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col justify-center items-center p-4 border-2 border-transparent peer-checked:border-black">
                  <span className="text-sm font-medium text-black mt-2">No</span>
                </div>
              </label>
              {/* Option: Yes */}
              <label className="cursor-pointer block">
                <input 
                  type="radio" 
                  name="differentModel" 
                  value="yes" 
                  className="hidden peer"
                  checked={differentModel === 'yes'}
                  onChange={() => setDifferentModel('yes')}
                />
                <div className="w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col justify-center items-center p-4 border-2 border-transparent peer-checked:border-black">
                  <span className="text-sm font-medium text-black mt-2">Yes</span>
                </div>
              </label>
            </div>
          </div>

          {/* Button Group */}
          <div className="flex space-x-4">
            <button 
              onClick={onClose} 
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
            >
              Close
            </button>
            <button 
              onClick={handleApply} 
              className={`px-4 py-2 bg-black text-white rounded ${isButtonDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'}`}
              disabled={isButtonDisabled}
            >
              Apply Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevisionSettings;